import {
  addToast,
  Button,
  CheckInput,
  Modal,
  TextInput,
} from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';

import { TranslationsKeys } from '../../../../../../locales/translations';
import { SelectOptionType } from '../../../types';
import { assignOrCreateSection } from '../api';
import { CreateSectionForm } from './CreateSectionForm';
import { SelectedSectionForm } from './SelectedSectionForm';
import {
  AssignedSection,
  AssignOrCreateSectionPayload,
  CourseWithSection,
} from '../types';

interface ModalAssignSectionProps {
  ceAcademicOfferId: number;
  onSuccess: () => void;
}

export interface ModalAssignSectionMethods {
  open: (course: CourseWithSection) => void;
  close: () => void;
}

interface FormData {
  // Para sección seleccionada
  selectedSection: SelectOptionType | null;
  selectedSectionData: AssignedSection | null;
  // Para crear nueva
  createNew: boolean;
  name: string;
  code: string;
  campus: SelectOptionType | null;
  quota: string;
  schedule: SelectOptionType | null;
  teachers: Array<{
    teacher: SelectOptionType | null;
    isOnline: boolean;
    isPresential: boolean;
  }>;
}

const defaultFormValues: FormData = {
  selectedSection: null,
  selectedSectionData: null,
  createNew: false,
  name: '',
  code: '',
  campus: null,
  quota: '0',
  schedule: null,
  teachers: [{ teacher: null, isOnline: false, isPresential: false }],
};

export function ModalAssignSection(
  { ceAcademicOfferId, onSuccess }: ModalAssignSectionProps,
  ref: Ref<ModalAssignSectionMethods>,
) {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const [open, setOpen] = useState(false);
  const [currentCourse, setCurrentCourse] = useState<CourseWithSection | null>(
    null,
  );
  const [showConflictAlert, setShowConflictAlert] = useState(false);

  const forms = useForm<FormData>({
    defaultValues: defaultFormValues,
  });

  const {
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { isSubmitting },
  } = forms;

  const createNew = watch('createNew');
  const selectedSection = watch('selectedSection');

  const handleOpen = useCallback(
    (course: CourseWithSection) => {
      setCurrentCourse(course);
      setOpen(true);
      reset(defaultFormValues);
    },
    [reset],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setCurrentCourse(null);
    setShowConflictAlert(false);
    reset(defaultFormValues);
  }, [reset]);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  // Cuando se selecciona una sección, cargar sus datos
  useEffect(() => {
    if (selectedSection && selectedSection.data) {
      setValue('selectedSectionData', selectedSection.data);
    } else {
      setValue('selectedSectionData', null);
    }
  }, [selectedSection, setValue]);

  const handleSave = async (data: FormData, forceAssign = false) => {
    if (!currentCourse) {
      return;
    }

    setShowConflictAlert(false);

    let payload: AssignOrCreateSectionPayload;

    if (data.createNew) {
      // Crear nueva sección
      payload = {
        ceAcademicOfferId,
        courseId: currentCourse.courseId,
        createNew: true,
        newSection: {
          name: data.name,
          code: data.code,
          campusId: data.campus!.value,
          maximumStudent: parseInt(data.quota, 10),
          scheduleId: data.schedule!.value,
          courseId: currentCourse.courseId,
          teachers: data.teachers
            .filter((t) => t.teacher !== null)
            .map((t, index) => ({
              teacherId: t.teacher!.value,
              isOnline: t.isOnline,
              isPresential: t.isPresential,
              order: index + 1,
            })),
        },
      };
    } else {
      // Asignar sección existente
      if (!data.selectedSection) {
        addToast({
          color: 'warning',
          text: t('sections.modal.selectSectionRequired'),
        });
        return;
      }

      payload = {
        ceAcademicOfferId,
        courseId: currentCourse.courseId,
        createNew: false,
        existingSection: {
          sectionId: data.selectedSection.value,
          courseId: currentCourse.courseId,
        },
        forceAssign,
      };
    }

    const { error } = await assignOrCreateSection(
      ceAcademicOfferId,
      currentCourse.courseId,
      payload,
    );

    if (error) {
      if (
        error.code === 'HTTP_ERROR' &&
        error.data?.message === 'SECTION_ALREADY_IN_OTHER_OFFER' &&
        !forceAssign
      ) {
        setShowConflictAlert(true);
      } else {
        addToast({
          color: 'danger',
          text: t('messages.error'),
        });
      }
      return;
    }

    addToast({
      color: 'success',
      text: t(
        data.createNew ? 'messages.sectionCreated' : 'messages.sectionAssigned',
      ),
    });

    handleClose();
    onSuccess();
  };

  const onSubmit = async (data: FormData) => {
    await handleSave(data, false);
  };

  const handleForceAssign = async () => {
    const data = watch();
    await handleSave(data, true);
  };

  if (!currentCourse) {
    return null;
  }

  return (
    <Modal isOpen={open} toggle={handleClose} size="xl">
      <Container fluid>
        <h1 className="text-dark fs-20 mt-3 mb-4 text-center">
          {t('sections.modal.title')}
        </h1>
        <p
          className="text-dark fs-16 mb-4 text-left"
          dangerouslySetInnerHTML={{
            __html: t('sections.modal.description'),
          }}
        />

        <form onSubmit={handleSubmit(onSubmit)}>
          <FormProvider {...forms}>
            <Row>
              <Col xs={12} className="mb-1">
                <TextInput
                  name={t('sections.modal.course')}
                  label={t('sections.modal.course')}
                  value={`${currentCourse.courseName} (${currentCourse.courseCode})`}
                  disabled
                />
                <CheckInput
                  name="createNew"
                  label={t('sections.modal.createNew')}
                  control={forms.control}
                />
              </Col>

              {!createNew ? (
                <SelectedSectionForm course={currentCourse} />
              ) : (
                <CreateSectionForm course={currentCourse} />
              )}

              <Col xs={12} className="py-3" />

              {/* Modal de conflicto */}
              <Modal
                isOpen={showConflictAlert}
                toggle={() => setShowConflictAlert(false)}
                size="md"
              >
                <Container fluid className="p-4">
                  <p className="text-dark fs-16 mb-4">
                    {t('sections.modal.conflictMessage')}
                  </p>

                  <div className="d-flex justify-content-center">
                    <Button
                      text={t('form.cancel')}
                      outlined
                      className="mr-4"
                      size="md"
                      style={{ width: '180px' }}
                      onClick={() => setShowConflictAlert(false)}
                    />
                    <Button
                      text={t('sections.modal.forceAssign')}
                      size="md"
                      style={{ width: '180px' }}
                      onClick={handleForceAssign}
                      disabled={isSubmitting}
                    />
                  </div>
                </Container>
              </Modal>
              <Col xs={12} md={6}>
                <Button
                  text={t('form.cancel')}
                  outlined
                  fullwidth
                  onClick={handleClose}
                  className="mb-3"
                />
              </Col>
              <Col xs={12} md={6}>
                <Button
                  text={t('sections.modal.save')}
                  type="submit"
                  fullwidth
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  className="mb-3"
                />
              </Col>
            </Row>
          </FormProvider>
        </form>
      </Container>
    </Modal>
  );
}

export default forwardRef(ModalAssignSection);

ModalAssignSection.displayName = 'ModalAssignSection';
